import React from 'react';

/**
 * Whether an element is on screen now (`inView`), and whether it has ever
 * been (`seen`, which latches).
 *
 * For work that should wait until the user can see its result, and stop when
 * they cannot: the dashboard radar card loads maplibre and asks the radar
 * service nothing until it has been seen, then refreshes only while in view.
 * Mobile's `RadarNowLite` waits for its row the same way.
 *
 * With no IntersectionObserver (a non-browser host), the element counts as
 * seen and in view, so the work still happens.
 */
const useSeenOnScreen = <T extends Element = HTMLDivElement>() => {
  const ref = React.useRef<T | null>(null);
  const unobservable = typeof IntersectionObserver === 'undefined';
  const [inView, setInView] = React.useState(unobservable);
  const [seen, setSeen] = React.useState(unobservable);

  React.useEffect(() => {
    const node = ref.current;
    if (node === null || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry === undefined) {
        return;
      }
      setInView(entry.isIntersecting);
      if (entry.isIntersecting) {
        setSeen(true);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, {seen, inView}] as const;
};

export default useSeenOnScreen;
